// Estado de la venta frente a La Central (Zentral): rótulo, tono y qué avisos se pueden reenviar.
import type { PillTone } from '@/shared/patterns/Pills';
import type {
    BackofficeSale,
    CentralCancelStatus,
    CentralResendKind,
    CentralSyncStatus,
} from '@/api/services/retail-wizard/contract';

export const CENTRAL_SYNC_STATUSES: readonly CentralSyncStatus[] = [
    'PENDING',
    'SENDING',
    'SENT',
    'ACCEPTED',
    'REJECTED',
    'FAILED',
    'N_A',
];

export const CENTRAL_CANCEL_STATUSES: readonly CentralCancelStatus[] = [
    'PENDING',
    'SENDING',
    'ACCEPTED',
    'REJECTED',
    'FAILED',
    'NOT_SENT',
];

/** Códigos con los que Zentral rechaza la pieza: reenviar no lo arregla. */
export const CENTRAL_PIECE_CONFLICT_CODES: ReadonlySet<string> = new Set([
    'IDENTIFIER_NOT_AVAILABLE',
    'INSUFFICIENT_STOCK',
]);

/** Teléfono del cliente sin 10 dígitos: el aviso no sale hasta corregirlo. */
export const CENTRAL_CUSTOMER_PHONE_INVALID = 'CENTRAL_CUSTOMER_PHONE_INVALID';

/** Estados de un aviso que se pueden reenviar: Zentral lo rechazó o el envío falló. */
export const RESENDABLE: ReadonlySet<string> = new Set(['REJECTED', 'FAILED']);

export type CentralSale = Pick<
    BackofficeSale,
    'status' | 'centralSyncStatus' | 'centralCancelStatus' | 'centralLastErrorCode'
>;

export interface CentralStatusView {
    /** Clave de i18n bajo `central.estado`. */
    key: string;
    defaultLabel: string;
    tone: PillTone;
}

const VIEW_BY_STATUS: Record<string, CentralStatusView> = {
    PENDING: { key: 'PENDING', defaultLabel: 'Pendiente', tone: 'warning' },
    SENDING: { key: 'SENDING', defaultLabel: 'Enviando', tone: 'warning' },
    SENT: { key: 'SENT', defaultLabel: 'Enviada', tone: 'info' },
    ACCEPTED: { key: 'ACCEPTED', defaultLabel: 'Aceptada', tone: 'success' },
    REJECTED: { key: 'REJECTED', defaultLabel: 'Rechazada', tone: 'danger' },
    FAILED: { key: 'FAILED', defaultLabel: 'Falló', tone: 'danger' },
    NOT_SENT: { key: 'NOT_SENT', defaultLabel: 'No se envía', tone: 'neutral' },
};

const NOT_APPLICABLE: CentralStatusView = {
    key: 'N_A',
    defaultLabel: 'No aplica',
    tone: 'neutral',
};

/** Rótulo y tono de un estado; N_A, ausente o desconocido se ve como «No aplica», nunca pendiente. */
export function centralStatusView(status: string | null | undefined): CentralStatusView {
    if (!status) return NOT_APPLICABLE;
    return VIEW_BY_STATUS[status] ?? NOT_APPLICABLE;
}

/** Lo que pinta la celda: la cancelación cuando ya tiene aviso propio, si no el registro. */
export function centralCellStatus(sale: CentralSale): string | undefined {
    const cancel = sale.centralCancelStatus;
    if (cancel && cancel !== 'NOT_SENT') return cancel;
    return sale.centralSyncStatus;
}

/** Zentral rechazó la pieza del registro (409): se cambia la pieza o se cancela, no se reenvía. */
export function hasCentralPieceConflict(sale: CentralSale): boolean {
    return (
        sale.centralSyncStatus === 'REJECTED' &&
        !RESENDABLE.has(sale.centralCancelStatus ?? '') &&
        CENTRAL_PIECE_CONFLICT_CODES.has(sale.centralLastErrorCode ?? '')
    );
}

/** Avisos que Operación puede reenviar; el registro de una venta cancelada nunca se reenvía. */
export function resendableKinds(sale: CentralSale): CentralResendKind[] {
    const kinds: CentralResendKind[] = [];
    if (
        RESENDABLE.has(sale.centralSyncStatus) &&
        sale.status !== 'CANCELLED' &&
        !hasCentralPieceConflict(sale)
    ) {
        kinds.push('SALE_REGISTER');
    }
    if (RESENDABLE.has(sale.centralCancelStatus ?? '')) kinds.push('SALE_CANCEL');
    return kinds;
}
